import { clamp } from "lodash";
import { websiteBuilderNormalizeValue } from "../schema/normalization/websiteBuilderNormalizeValue";
import { TWebsiteBuilderElement } from "./element/websiteBuilderElement";
import { TWebsiteBuilderContainer } from "./container/websiteBuilderContainer";
import { TWebsiteBuilderDevice } from "../website/shared/device";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "./websiteBuilderNodeGeometry";

export const WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH = 25;
export const WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT = 25;

export const WEBSITE_BUILDER_SECTION_ENTITY_STRETCH_MIN_MARGIN = 0;
export const WEBSITE_BUILDER_SECTION_ENTITY_STRETCH_MAX_MARGIN = 100;

/**
* A SectionEntity is a content node that can exist directly within a Section.  
*  
* SectionEntity nodes can also appear nested inside layout structures such as Containers or GridCells.
* Nodes like a GridCell are not considered a SectionEntity since although they organize SectionEntities, they can not exist directly within a Section.
*/

export type TWebsiteBuilderSectionEntity = TWebsiteBuilderElement | TWebsiteBuilderContainer;
export type TWebsiteBuilderSectionEntityType = TWebsiteBuilderSectionEntity["type"];

export function isWebsiteBuilderSectionEntity(node: any): node is TWebsiteBuilderSectionEntity {
  return node?.type === "element" || node?.type === "container";
}

export interface IWebsiteBuilderSectionEntityLayoutData {
  position: IWebsiteBuilderNodePosition;
  dimensions: IWebsiteBuilderNodeDimensions;
}

export type TWebsiteBuilderSectionEntityLayout = {
  [K in TWebsiteBuilderDevice]: IWebsiteBuilderSectionEntityLayoutData;
}

export type TWebsiteBuilderSectionEntityResizeHandle =
  | "NorthWest"
  | "North"
  | "NorthEast"
  | "West"
  | "East"
  | "SouthWest"
  | "South"
  | "SouthEast";

export type TWebsiteBuilderSectionEntityResizeHandleCategory = TWebsiteBuilderSectionEntityResizeHandle[];

export interface IWebsiteBuilderSectionEntityMoveMouseOffset {
  x: number;
  y: number;
}

export interface IWebsiteBuilderSectionEntityStretch {
  enabled: boolean;
  margin: number;
}

export type TWebsiteBuilderSectionEntityHasStretch<T> = T extends { stretch: IWebsiteBuilderSectionEntityStretch } ? T : never;

export function websiteBuilderNormalizeSectionEntityLayout(partialLayout: Partial<TWebsiteBuilderSectionEntityLayout>): TWebsiteBuilderSectionEntityLayout {
  const normalizeLayoutValue = (defaultValue: number) => (value: unknown): number => {
    return websiteBuilderNormalizeValue({
      type: "number",
      value,
      defaultValue,
      required: true,
    });
  };
  
  const normalizeCoordinate = normalizeLayoutValue(0);
  const normalizeWidth = normalizeLayoutValue(WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_WIDTH);
  const normalizeHeight = normalizeLayoutValue(WEBSITE_BUILDER_SECTION_ENTITY_MINIMUM_HEIGHT);
  
  const normalizedLayout: TWebsiteBuilderSectionEntityLayout = {
    desktop: {
      position: {
        x: normalizeCoordinate(partialLayout.desktop.position.x),
        y: normalizeCoordinate(partialLayout.desktop.position.y),
      },
      dimensions: {
        width: normalizeWidth(partialLayout.desktop.dimensions.width),
        height: normalizeHeight(partialLayout.desktop.dimensions.height),
      },
    },
    tablet: {
      position: {
        x: normalizeCoordinate(partialLayout.tablet.position.x),
        y: normalizeCoordinate(partialLayout.tablet.position.y),
      },
      dimensions: {
        width: normalizeWidth(partialLayout.tablet.dimensions.width),
        height: normalizeHeight(partialLayout.tablet.dimensions.height),
      },
    },
    mobile: {
      position: {
        x: normalizeCoordinate(partialLayout.mobile.position.x),
        y: normalizeCoordinate(partialLayout.mobile.position.y),
      },
      dimensions: {
        width: normalizeWidth(partialLayout.mobile.dimensions.width),
        height: normalizeHeight(partialLayout.mobile.dimensions.height),
      },
    },
  };

  return normalizedLayout;
}

export function websiteBuilderGenerateDefaultSectionEntityStretch(): IWebsiteBuilderSectionEntityStretch {
  return {
    enabled: false,
    margin: 0,
  };
}

export function websiteBuilderNormalizeSectionEntityStretch(partialStretch: Partial<IWebsiteBuilderSectionEntityStretch>): IWebsiteBuilderSectionEntityStretch {
  const defaultStretch = websiteBuilderGenerateDefaultSectionEntityStretch();
  
  if (partialStretch == null) {
    return defaultStretch;
  }

  const normalizedStretch: IWebsiteBuilderSectionEntityStretch = {
    enabled: websiteBuilderNormalizeValue({
      type: "boolean",
      value: partialStretch.enabled,
      defaultValue: defaultStretch.enabled,
      required: true,
    }),
    margin: websiteBuilderNormalizeValue({
      type: "number",
      value: partialStretch.margin,
      defaultValue: defaultStretch.margin,
      required: true,
      transform: (v: number) => clamp(v, WEBSITE_BUILDER_SECTION_ENTITY_STRETCH_MIN_MARGIN, WEBSITE_BUILDER_SECTION_ENTITY_STRETCH_MAX_MARGIN),
    }),
  };

  return normalizedStretch;
}

export function websiteBuilderGetSectionEntityActiveLayoutData(node: TWebsiteBuilderSectionEntity, device: TWebsiteBuilderDevice): IWebsiteBuilderSectionEntityLayoutData {
  if (node.type === "element") {
    return node.element.layout[device];
  } else {
    return node.container.layout[device];
  }
}